// The compute wire contract — owned by the C++ ReconciliationWrapper
// (SPEC/1489). Field names are snake_case to match the JSON the worker
// forwards to cgo verbatim. A reconciliation's `config` and an instance's
// `configSnapshot` ARE a BatchRequest.

export interface FileSpec {
  id: string;
}

export interface ReaderSpec {
  id: string;
  /** A storage-hub file id at dispatch time; the worker substitutes the local path before cgo. */
  path: string;
  delimiter: string;
  has_header: boolean;
  coordinates_indexes: [number, number, number];
  delta: [number, number, number];
  min?: [number, number, number];
  max?: [number, number, number];
}

/** Per-column missing-data policy (optional). */
export interface MissingData {
  policy: 'Ok' | 'Delete' | 'SetTo';
  anomalous_value?: string;
  replacement_value?: string;
}

export interface AlgorithmModelColumn {
  index: number;
  name: string;
  missing_data?: MissingData;
}

export interface AlgorithmModel {
  reader_id: string;
  columns: AlgorithmModelColumn[];
}

/** Measures the tonnage algorithms can plot. The Lib always computes the three
 *  (totalTonnages/totalGrades/totalMetals); this only selects which ones the
 *  dashboard draws. 2 → dual-axis chart, 3 → stacked panels. */
export type PlotMeasure = 'tonnage' | 'metal' | 'grade';

/** The reference each error metric divides by: LP, CP, or the mean of both (the
 *  symmetric SP). The Lib always computes all three from the same sums; this only
 *  selects which ones the dashboard draws — one chart per selected base. */
export type ErrorBase = 'lp' | 'cp' | 'sp';

/** Display order, so the charts always come out in the same sequence regardless
 *  of the order the user ticked them. */
export const ERROR_BASE_ORDER: ErrorBase[] = ['lp', 'cp', 'sp'];

/** The legacy conciliatron's own acronyms, which is what the client's historical
 *  reports carry — so the selector, the titles and the axes all name the metric the
 *  same way. The mapping from its internal letters lives in
 *  `modulosPy/figuresAnalytics/byperiodMovErrorGtm.py` ('L' → RSE_LP, 'C' → RSE_CP,
 *  'B' → MSRE). */
export const ERROR_BASE_LABELS: Record<ErrorBase, string> = {
  lp: 'RSE_LP',
  cp: 'RSE_CP',
  sp: 'MSRE',
};

/** One curve by default: with small deviations the three metrics differ by
 *  hundredths of a point, so drawing them together reads as a single thick line.
 *  Anyone who wants to compare ticks another — and since `error_bases` is
 *  presentation-only it redraws with no re-run. */
export const DEFAULT_ERROR_BASES: ErrorBase[] = ['lp'];

/** The single source of truth for which error bases a chart draws: an absent,
 *  empty or unrecognizable selection resolves to the default, and the result is
 *  ordered. Both the selector (UI) and the chart mapper go through this.
 *
 *  Never returns an empty list, and that is the point: `algorithmResultToCharts`
 *  keeps only the intents whose base is listed here, so `[]` would drop every
 *  error chart and the figure would leave the dashboard with nothing said. A
 *  persisted config can carry a base this build does not know (an old spelling, a
 *  hand edit, a rename in the Lib), so the filter alone is not enough. */
export function resolveErrorBases(bases: ErrorBase[] | undefined): ErrorBase[] {
  const chosen = bases && bases.length > 0 ? bases : DEFAULT_ERROR_BASES;
  const ordered = ERROR_BASE_ORDER.filter((b) => chosen.includes(b));
  return ordered.length > 0 ? ordered : DEFAULT_ERROR_BASES;
}

export type FilterType = 'spatial' | 'combined' | 'categorical' | 'continuous' | 'true' | 'false';

export interface SpatialFilter {
  id: string;
  type: 'spatial';
  /** storage-hub file ids of solid files (STL/DXF); resolved by the worker. */
  solids: string[];
}
export interface CombinedFilter {
  id: string;
  type: 'combined';
  /** boolean expression over other filter ids, e.g. "fA AND NOT fB". */
  expression: string;
}
export interface CategoricalFilter {
  id: string;
  type: 'categorical';
  /** position into the bound model's columns[] (the model this filter gates). */
  category_column: number;
  category_value: string;
}
export interface ContinuousFilter {
  id: string;
  type: 'continuous';
  /** position into the bound model's columns[]. */
  continuous_column: number;
  min_limit_value?: number;
  max_limit_value?: number;
}
export interface PassFilter {
  id: string;
  type: 'true' | 'false';
}
export type FilterSpec =
  | SpatialFilter
  | CombinedFilter
  | CategoricalFilter
  | ContinuousFilter
  | PassFilter;

/**
 * One entry of the opaque `algorithms` array. The conciliatron backend
 * never parses this — the C++ wrapper owns its schema — so the frontend
 * is the sole author. `parameters` carries the per-algorithm knobs;
 * `filters`/`model_filters` are the optional per-algorithm filter surface
 * (SPEC/1489), and `criterio_seleccion` defaults to "Coexistencia".
 */
export interface AlgorithmEntry {
  name: string;
  models: AlgorithmModel[];
  parameters: Record<string, unknown>;
  filters?: FilterSpec[];
  /** 2 entries (filter id or null=pass-all); one per model. */
  model_filters?: (string | null)[];
  criterio_seleccion?: string;
  /** Which measures the tonnage charts plot (2–3). Presentation-only: persisted
   *  in the reconciliation config and read by resultToDashboard; NOT a Lib
   *  parameter (the wrapper never parses this entry). */
  plot_measures?: PlotMeasure[];
  /**
   * Stable identity of the configured chart this entry came from — the key that
   * ties a draft algorithm, its result envelope and its chart(s) together across
   * edits (see lib/algorithmInstance). UI-only, like `plot_measures`: the Go
   * backend keeps `algorithms` as an opaque blob and the C++ wrapper reads only
   * the keys it knows, so an extra one rides along untouched.
   *
   * Optional because configs saved before minting have none; whoever reads them
   * materialises it positionally (`ensureInstanceIds`), which reproduces exactly
   * the id that scheme implied.
   *
   * The tonnage by-category fan-out emits several entries from ONE configured
   * chart; they all carry that chart's id, which is what makes the fan-out
   * resolve back to a single draft entry.
   */
  instance_id?: string;
  /** Which error metrics the `error` chart draws (one chart per base).
   *  Presentation-only, same contract as `plot_measures`: persisted in the config
   *  and read by resultToDashboard, NOT a Lib parameter. */
  error_bases?: ErrorBase[];
  /** The `error` chart's tolerance in percent: draws dashed black lines at ±t
   *  on each error chart, the Lib's percent scale (see resultToDashboard's
   *  toleranceLimits, SPEC/2620). Presentation-only, same contract as
   *  `error_bases` (SPEC/2423). The cut-off has no key: it travels as a filter. */
  error_tolerance?: number;
  /**
   * User-facing name of this configured chart — the single name shown as the
   * figure's title everywhere: the modal header / tree label, the "Nombre" field
   * in the parameters tab, and the chart's own title. UI-only, same contract as
   * `instance_id`: the Go backend and the C++ wrapper never read it. See SPEC/2240.
   *
   * An instance that draws several figures from one run (the `error` algorithm's
   * LP/CP/SP bases, the tonnage-by-category fan-out) still gets this name on
   * every figure it produces: resultToDashboard applies it bare to the first and
   * suffixes "#N" (N≥2) on each subsequent one, in the order the figures are walked.
   */
  figure_title?: string;
}

export interface BatchRequest {
  files?: FileSpec[];
  readers: ReaderSpec[];
  algorithms: AlgorithmEntry[];
}
